import { useMemo } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { toast } from 'sonner'

import { ScentingLotStockInForm } from './new'
import type { CreateInNoteRequest } from '@/lib/api/scenting-lot-api'
import type { LotOption } from '@/components/lots/lot-items-field'
import { scentingNoteItemOption } from '@/components/lots/lot-option-builders'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/hooks/use-auth'
import {
    useScentingInNote,
    useUpdateScentingInNote,
} from '@/hooks/use-scenting-lot-query'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/scenting-warehouse/stock-in-notes/$id_/edit',
)({
    component: Page,
})

function Page() {
    const { id } = Route.useParams()
    const navigate = useNavigate()
    const { user } = useAuth()
    const { data, isLoading } = useScentingInNote(id)
    const note = data?.data
    const update = useUpdateScentingInNote()

    const seedLotOptions = useMemo(
        () => (note?.items ?? []).map(scentingNoteItemOption),
        [note],
    )
    // Lô phân loại nguồn đã chọn (mọi dòng) — seed để picker hiện đúng dù note đã
    // trừ cạn.
    const seedSourceOptions = useMemo<LotOption[]>(
        () =>
            (note?.items ?? []).flatMap((it) =>
                (it.sources ?? []).map((s) => ({
                    value: s.source_sorting_lot_id,
                    label: s.source_sorting_lot_code ?? s.source_sorting_lot_id,
                })),
            ),
        [note],
    )

    if (isLoading) return <div className="p-6">{m.sl_detail_loading()}</div>
    if (!note) return <div className="p-6">{m.sl_detail_note_not_found()}</div>

    const isCreator = !!user?.id && note.created_by_id === user.id
    const editable =
        note.status === 'pending' && isCreator && !note.stocktake_session_id

    if (!editable) {
        return (
            <div className="space-y-4 p-6">
                <Button asChild variant="ghost" className="-ml-2">
                    <Link
                        to="/scenting-warehouse/stock-in-notes/$id"
                        params={{ id }}
                    >
                        <ArrowLeft className="size-4 mr-1" />
                        {m.sl_lot_back()}
                    </Link>
                </Button>
                <p className="text-muted-foreground">
                    {m.pl_pnk_edit_not_allowed()}
                </p>
            </div>
        )
    }

    const initialValues = {
        // Phiếu sửa được chỉ có 3 lý do này (stocktake bị khoá ở guard trên).
        reason: note.reason as
            | 'adjustment'
            | 'from_process'
            | 'return_from_packaging_process',
        notes: note.notes ?? '',
        items: (note.items ?? []).map((it) => ({
            kind: 'existing' as const,
            scenting_lot_id: it.scenting_lot_id,
            quantity: it.quantity,
            sources: (it.sources ?? []).map((s) => ({
                source_lot_id: s.source_sorting_lot_id,
                source_quantity: s.source_quantity,
            })),
        })),
    }

    const backToDetail = () =>
        navigate({
            to: '/scenting-warehouse/stock-in-notes/$id',
            params: { id },
        })

    const handleSubmit = async (value: CreateInNoteRequest) => {
        try {
            await update.mutateAsync({
                id,
                req: {
                    reason: value.reason,
                    source_withdrawal_note_id: value.source_withdrawal_note_id,
                    notes: value.notes,
                    items: value.items,
                },
            })
            void backToDetail()
        } catch (err) {
            toast.error(
                err instanceof Error ? err.message : m.sl_new_error_create(),
            )
        }
    }

    return (
        <ScentingLotStockInForm
            mode="edit"
            title={m.scl_pnk_edit_title()}
            submitLabel={m.pl_pnk_edit_submit()}
            initialValues={initialValues}
            initialWithdrawalNoteId={
                note.sorting_lot_withdrawal_note_id ?? undefined
            }
            seedLotOptions={seedLotOptions}
            seedSourceOptions={seedSourceOptions}
            isPending={update.isPending}
            onSubmit={(value) => void handleSubmit(value)}
            onCancel={() => void backToDetail()}
        />
    )
}
